import type { ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { Activity, Bot, Mail, Ship as ShipIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { formatShortDateTime } from '@/lib/formatters'
import type { ThreadRow, TimelineEvent } from './types'

export function ThreadsTab({ threads }: { threads: ThreadRow[] }) {
  if (threads.length === 0) {
    return <p className="text-sm text-muted-foreground">No threads linked to this shipment.</p>
  }

  return (
    <div className="space-y-0 rounded-md border">
      {threads.map((thread) => (
        <div
          key={thread.id}
          className="flex items-start gap-3 border-b last:border-0 px-4 py-3 hover:bg-muted/30 cursor-pointer"
          onClick={() => router.visit(thread.show_url)}
        >
          <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-foreground truncate">
                {thread.subject || '(no subject)'}
              </span>
              <Badge
                variant="secondary"
                size="sm"
                className="font-medium shrink-0 bg-gray-100 text-gray-600"
              >
                {thread.emails_count} {thread.emails_count === 1 ? 'email' : 'emails'}
              </Badge>
            </div>
            <p className="text-[12px] text-muted-foreground truncate mt-0.5">
              {thread.participants.slice(0, 3).join(', ')}
              {thread.participants.length > 3 && ` +${thread.participants.length - 3} more`}
            </p>
          </div>
          <span className="text-[11px] text-muted-foreground shrink-0 mt-0.5">
            {formatShortDateTime(thread.latest_date)}
          </span>
        </div>
      ))}
    </div>
  )
}

const TIMELINE_TYPE_ICONS: Record<string, ReactNode> = {
  email: <Mail className="h-3.5 w-3.5" />,
  agent_session: <Bot className="h-3.5 w-3.5" />,
  booking: <ShipIcon className="h-3.5 w-3.5" />,
}

const TIMELINE_ACTOR_COLORS: Record<string, string> = {
  operator: 'text-primary',
  external: 'text-muted-foreground',
  system: 'text-muted-foreground/60',
}

export function ShipmentTimelineTab({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground italic">No activity yet</p>
  }

  return (
    <section>
      <h3 className="text-[13px] font-semibold text-foreground mb-3">Activity Timeline</h3>
      <div className="space-y-0">
        {events.map((event, i) => (
          <div key={i} className="flex gap-3 py-2.5 border-b last:border-0">
            <div className={`mt-0.5 ${TIMELINE_ACTOR_COLORS[event.actor] || 'text-muted-foreground'}`}>
              {TIMELINE_TYPE_ICONS[event.type] || <Activity className="h-3.5 w-3.5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-foreground truncate">{event.description || 'No description'}</p>
              <p className="text-[10px] text-muted-foreground">
                {event.date ? formatShortDateTime(event.date) : ''} {'\u00B7'} {event.actor}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
